import React, { useState, useEffect } from 'react';
import { Box, Fade, Radio, RadioGroup, Checkbox, FormControlLabel } from "@mui/material";
import UIText from "@/components/text/UIText";
import UIChip from '../chips/UIChip';
import colors from '@/utils/colorsV2';

export interface FilterOptions {
  aseguradora: string;
  sucursal: string;
  agenteId: string[];
  searchTerm: string;
  ordenarPor: string;
  estatus: {
    solicitudCompleta: boolean;
    enProceso: boolean;
    enProcesoFirma: boolean;
    errorEnSistema: boolean;
  };
}

export interface FilterSidebarProps {
  filters: FilterOptions;
  onApplyFilters: (filters: FilterOptions) => void;
  isOpen: boolean;
  onClose: () => void;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({ filters, onApplyFilters, isOpen, onClose }) => {
  const [tempFilters, setTempFilters] = useState<FilterOptions>(filters);

  const styles = {
    filterContainer: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "flex-start",
      padding: "24px",
      gap: "16px",
      position: "fixed" as const,
      width: "288px",
      height: "auto",
      maxHeight: "calc(100vh - 320px)",
      right: "55px",
      top: "274px",
      backgroundColor: "#FFFFFF",
      boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.18)",
      borderRadius: "8px",
      transform: isOpen ? "translateX(0)" : "translateX(100%)",
      transition: "transform 0.3s ease-in-out",
      zIndex: 1300,
      overflowY: "auto" as const,
      '@media (max-width:1200px)': {
        top: "355px",
        height: "auto",
        width: "auto",
        padding: "20px"
      }
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      marginBottom: "8px"
    },
    section: {
      width: "100%",
    },
    radioGroup: {
      '& .MuiFormControlLabel-root': {
        marginBottom: "4px"
      }
    },
    checkboxGroup: {
      '& .MuiFormControlLabel-root': {
        marginBottom: "4px"
      },
      display: "flex",
      flexDirection: "column" as const,
    },
    check: {
      '&.Mui-checked': {
        color: '#F9A11B',
      },
      '&.Mui-checked .MuiSvgIcon-root path': {
        fill: '#F9A11B',
      },
    },
    radio: {
      '&.Mui-checked': {
        color: '#F9A11B',
      },
    },
    clearButton: {
      width: "100%",
      height: "36px",
      backgroundColor: "#FFFFFF",
      border: "1px solid #D0D5DD",
      borderRadius: "8px",
      color: "#344054",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s",
      '&:hover': {
        backgroundColor: "#F9FAFB",
      }
    }
  };

  useEffect(() => {
    setTempFilters(filters);
  }, [filters, isOpen]);

  const handleTempFilterChange = (key: keyof FilterOptions, value: any) => {
    const newFilters = {
      ...tempFilters,
      [key]: value
    };
    setTempFilters(newFilters);
    onApplyFilters(newFilters);
  };

  const handleEstatusChange = (estatusKey: keyof FilterOptions['estatus'], checked: boolean) => {
    const newFilters = {
      ...tempFilters,
      estatus: {
        ...tempFilters.estatus,
        [estatusKey]: checked
      }
    };
    setTempFilters(newFilters);
    onApplyFilters(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters: FilterOptions = {
      ...tempFilters,
      ordenarPor: '',
      estatus: {
        solicitudCompleta: false,
        enProceso: false,
        enProcesoFirma: false,
        errorEnSistema: false,
      }
    };
    setTempFilters(clearedFilters);
    onApplyFilters(clearedFilters);
  };

  return (
    <Fade in={isOpen}>
      <Box sx={styles.filterContainer}>
        {/* Header */}
        <Box sx={styles.header}>
          <UIText text="Filtros" size="text-lg" color="blue" weight="semibold" />
        </Box>
        {/* Ordenar por */}
        <Box sx={styles.section}>
          <UIText
            text="Ordenar por"
            size="text-md"
            color="gray"
            weight="medium"
            sx={{ marginBottom: "8px" }}
          />
          <RadioGroup
            value={tempFilters.ordenarPor}
            onChange={(e) => handleTempFilterChange('ordenarPor', e.target.value)}
            sx={styles.radioGroup}
          >
            <FormControlLabel
              value="antiguo"
              control={<Radio size="small" sx={styles.radio} />}
              label={<UIText text="El más antiguo primero" size="text-s" color="gray" />}
            />
            <FormControlLabel
              value="reciente"
              control={<Radio size="small" sx={styles.radio} />}
              label={<UIText text="El más reciente primero" size="text-s" color="gray" />}
            />
          </RadioGroup>
        </Box>

        {/* Estatus Section */}
        <Box sx={styles.section}>
          <UIText
            text="Estatus"
            size="text-md"
            color="gray"
            weight="medium"
            sx={{ marginBottom: "8px" }}
          />
          <Box sx={styles.checkboxGroup}>
            <FormControlLabel
              control={<Checkbox
                checked={tempFilters.estatus.solicitudCompleta}
                onChange={(e) => handleEstatusChange('solicitudCompleta', e.target.checked)}
                size="small"
                sx={styles.check}
              />}
              label={<UIChip label="Solicitud completa" type="success" />}
            />
            <FormControlLabel
              control={<Checkbox
                checked={tempFilters.estatus.enProceso}
                onChange={(e) => handleEstatusChange('enProceso', e.target.checked)}
                size="small"
                sx={styles.check}
              />}
              label={<UIChip label="En proceso" type="info" />}
            />
            
            <FormControlLabel
              control={<Checkbox
                checked={tempFilters.estatus.enProcesoFirma}
                onChange={(e) => handleEstatusChange('enProcesoFirma', e.target.checked)}
                size="small"
                sx={styles.check}
              />}
              label={<UIChip label="En firma" type="purple" />}
            />
            <FormControlLabel
              control={<Checkbox
                checked={tempFilters.estatus.errorEnSistema}
                onChange={(e) => handleEstatusChange('errorEnSistema', e.target.checked)}
                size="small"
                sx={styles.check}
              />}
              label={<UIChip label="Error" type="error" />}
            />
          </Box>
        </Box>


        {/* Botón Limpiar Filtros */}
        <Box sx={{ width: "100%"}}>

            <div
              onMouseDown={handleClearFilters}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                cursor: 'pointer',
                color: '#2563EB',
                fontSize: '14px',
                fontWeight: '500',
                width: '100%'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 4H3.33333H14" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5.33331 4.00016V2.66683C5.33331 2.31321 5.47379 1.97407 5.72384 1.72402C5.97389 1.47397 6.31302 1.3335 6.66665 1.3335H9.33331C9.68694 1.3335 10.0261 1.47397 10.2761 1.72402C10.5262 1.97407 10.6666 2.31321 10.6666 2.66683V4.00016M12.6666 4.00016V13.3335C12.6666 13.6871 12.5262 14.0263 12.2761 14.2763C12.0261 14.5264 11.687 14.6668 11.3333 14.6668H4.66665C4.31302 14.6668 3.97389 14.5264 3.72384 14.2763C3.47379 14.0263 3.33331 13.6871 3.33331 13.3335V4.00016H12.6666Z" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Borrar selección
            </div>
        </Box>
      </Box>
    </Fade>
  );
};

export default FilterSidebar;